import type { TournamentCompetitionConflict, TournamentCompetitionEvent } from "@mixitone/models";

export interface TournamentConflict {
  id: string;
  conflict_type: string;
  event_ids: string[];
  payload: Record<string, unknown>;
  status: "open" | "resolved";
}

export interface WinnerConflictContext {
  existingEvent: TournamentCompetitionEvent | null;
  existingWinnerId?: string;
  nextEvent: TournamentCompetitionEvent;
  nextWinnerId?: string;
  slotId: string;
}

export function sameClientWinnerCorrection(
  existingEvent: TournamentCompetitionEvent | null,
  nextEvent: TournamentCompetitionEvent,
): boolean {
  const existingClientId = existingEvent?.client_id;
  const nextClientId = nextEvent.client_id;
  return Boolean(existingClientId && nextClientId && existingClientId === nextClientId);
}

export function isAuthoritativeWinnerPayload(payload: Record<string, unknown>): boolean {
  return payload.authoritative === true;
}

export function dropWinnerConflictsForSlot(conflicts: TournamentConflict[], slotId: string): void {
  for (let index = conflicts.length - 1; index >= 0; index--) {
    const conflict = conflicts[index];
    if (conflict.conflict_type === "winner_conflict" && conflict.payload?.slot_id === slotId) {
      conflicts.splice(index, 1);
    }
  }
}

export function detectWinnerConflict(context: WinnerConflictContext): TournamentConflict | undefined {
  const { existingEvent, existingWinnerId, nextEvent, nextWinnerId, slotId } = context;
  if (!existingEvent || !existingWinnerId || !nextWinnerId || existingWinnerId === nextWinnerId) {
    return;
  }
  if (sameClientWinnerCorrection(existingEvent, nextEvent)) {
    return;
  }

  return {
    id: `winner-conflict:${slotId}:${existingEvent.id}:${nextEvent.id}`,
    conflict_type: "winner_conflict",
    event_ids: [existingEvent.id!, nextEvent.id!],
    payload: {
      slot_id: slotId,
      existing_winner_id: existingWinnerId,
      next_winner_id: nextWinnerId,
    },
    status: "open",
  };
}

export interface VoidResultConflictContext {
  existingEvent: TournamentCompetitionEvent | null;
  existingStatus?: string;
  existingWinnerId?: string;
  nextEvent: TournamentCompetitionEvent;
  slotId: string;
}

/**
 * Concurrent void vs result: keep the earlier terminal state and surface a
 * conflict instead of silently dropping the later event.
 */
export function detectVoidResultConflict(context: VoidResultConflictContext): TournamentConflict | undefined {
  const { existingEvent, existingStatus, existingWinnerId, nextEvent, slotId } = context;
  const nextType = nextEvent.event_type;
  const isNextResult =
    nextType === "match_completed" || nextType === "winner_selected" || nextType === "match_result_recorded";
  const isNextVoid = nextType === "match_voided";
  const hasResult = existingStatus === "completed" || !!existingWinnerId;
  const isVoided = existingStatus === "voided";

  if (!((isVoided && isNextResult) || (hasResult && isNextVoid))) {
    return;
  }

  const existingId = existingEvent?.id ?? "unknown";
  const nextId = nextEvent.id ?? "unknown";
  return {
    id: `void-result-conflict:${slotId}:${existingId}:${nextId}`,
    conflict_type: "void_result_conflict",
    event_ids: [existingId, nextId].filter((id) => id !== "unknown"),
    payload: {
      slot_id: slotId,
      existing_status: existingStatus,
      existing_winner_id: existingWinnerId,
      existing_event_type: existingEvent?.event_type,
      next_event_type: nextType,
    },
    status: "open",
  };
}

export function mergeOpenConflicts(
  persisted: TournamentCompetitionConflict[],
  detected: TournamentConflict[],
): TournamentConflict[] {
  const resolvedConflictIds = new Set(
    persisted
      .filter((conflict) => conflict.status === "resolved" && !conflict.is_deleted)
      .map((conflict) => conflict.conflict_key || conflict.id!)
      .filter(Boolean),
  );

  // Only currently detected disagreements stay open. Persisted opens that the
  // reducer no longer emits (same-client corrections, authoritative resolves)
  // must not keep the UI blocked.
  const byId = new Map<string, TournamentConflict>();
  for (const conflict of detected) {
    if (resolvedConflictIds.has(conflict.id)) continue;
    byId.set(conflict.id, conflict);
  }
  return [...byId.values()];
}
